// audioChunk.js — shared guard for MediaRecorder chunk uploads.
//
// MediaRecorder occasionally emits a non-zero but *headerless* blob when a
// recorder is torn down and a new one started in the same tick (the classic
// "stop then immediately start again" race). Those bytes decode to nothing —
// every transcription backend (ffmpeg/self-hosted, Groq, OpenAI) rejects them
// as "invalid media" — so they must never be shipped. This sniffs the leading
// bytes for a recognizable container header before we send.

/**
 * True if the blob starts with a recognizable media-container header that a
 * transcription backend can decode. Covers the formats MediaRecorder produces
 * across browsers: WebM/Matroska (Chrome/Firefox), MP4/ISO-BMFF (Safari), Ogg.
 *
 * Fails OPEN: if we somehow can't read the bytes, returns true so the backend
 * (not this guard) makes the final call — we only ever drop a chunk we're sure
 * is junk.
 */
export const looksLikeMediaContainer = async (blob) => {
  try {
    if (!blob || blob.size === 0) return false;
    const head = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    if (head.length < 4) return false;
    // WebM / Matroska — EBML magic: 1A 45 DF A3
    if (
      head[0] === 0x1a &&
      head[1] === 0x45 &&
      head[2] === 0xdf &&
      head[3] === 0xa3
    ) {
      return true;
    }
    // Ogg — "OggS"
    if (
      head[0] === 0x4f &&
      head[1] === 0x67 &&
      head[2] === 0x67 &&
      head[3] === 0x53
    ) {
      return true;
    }
    // MP4 / ISO-BMFF — bytes 4..8 are the "ftyp" box type
    if (
      head[4] === 0x66 &&
      head[5] === 0x74 &&
      head[6] === 0x79 &&
      head[7] === 0x70
    ) {
      return true;
    }
    return false;
  } catch (e) {
    // Can't inspect — don't block; let the backend decide.
    return true;
  }
};

export default looksLikeMediaContainer;
